import styles from './FilterCard.module.scss'

/**
 * Uniform card container for a single filter in the catalog filters panel.
 *
 * Provides a consistent header (label + optional strict-match checkbox) and a
 * children slot for any input primitive (RangeSlider, MultiSelect, etc.).
 * The card has a fixed min-height so cards in the same grid row stay aligned
 * regardless of the input type inside.
 *
 * The visible border accent (`isActive`) signals that this filter is currently
 * narrowing the result set — useful visual feedback without requiring the user
 * to inspect the input value.
 *
 * @param {object}        props
 * @param {string}        props.label                 - displayed filter name (i18n string)
 * @param {React.ReactNode} [props.valueSummary]       - current value digest shown in header
 *                                                       e.g. "od 2 do 4" for RangeSlider;
 *                                                       omit for MultiSelect (chips handle it)
 * @param {boolean}       [props.isActive=false]      - accent border when filter is active
 * @param {boolean}       [props.hideLabel=false]      - omit the label element (use when the input carries its own label, e.g. MultiSelect summary variant)
 * @param {React.ReactNode} props.children            - input slot
 * @param {string}        [props.className]           - extra class on root element
 */
export function FilterCard({
  label,
  valueSummary,
  isActive = false,
  hideLabel = false,
  children,
  className,
}) {
  const rootClass = [
    styles.card,
    isActive ? styles.active : null,
    className,
  ].filter(Boolean).join(' ')

  return (
    <div className={rootClass}>
      <div className={styles.header}>
        {!hideLabel && (
          <span className={styles.label}>{label}</span>
        )}

        {valueSummary != null && (
          <span className={styles.valueSummary}>{valueSummary}</span>
        )}
      </div>

      <div className={styles.body}>
        {children}
      </div>
    </div>
  )
}
